import React from 'react';
import { Divider, Input, Typography, Box, Button, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { HiOutlineSearch } from 'react-icons/hi';
import { toast } from 'react-toastify';
import ActionDrivenTable from '../../actionDrivenTable/ActionDrivenTable';
import DetailsPage from '../../detailsPage';
import PageSection from '../../pageSection/PageSection';
import { ShipyardContext } from '../../Context';
import API from '@aws-amplify/api';
import useDebounce from '../../../utils/useDebounce/useDebounce';
import { useHistory } from 'react-router-dom';
import { t } from 'i18next';
import RenderIf from '../../renderIf/RenderIf';

const MoveRecordPage = () => {
    const ship = React.useContext(ShipyardContext);
    const { moveConfig } = ship;

    const [selectedRecordToMerge, setSelectedRecordToMerge] = React.useState(null);
    const [searchKeys, setSearchKeys] = React.useState([]);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [confirmModalIsOpen, setConfirmModalIsOpen] = React.useState(false);
    const [targetHeaders, setTargetHeaders] = React.useState([]);
    const [target, setTarget] = React.useState({});
    const [moveList, setMoveList] = React.useState([]);
    const [moveListHeaders, setMoveListHeaders] = React.useState([]);
    const [modalQuestions, setModalQuestions] = React.useState([]);
    const [doingAjax, setDoingAjax] = React.useState(false);

    const history = useHistory();

    const handleBackClick = () => {
        history.goBack();
    };

    const getRecords = async (term) => {
        setDoingAjax(true);
        const buildFilterString = () => {
            if (!searchTerm) {
                return [''];
            }
            return searchKeys.map((key) => `\`${key}\` LIKE '%${term}%'`);
        };

        try {
            let res = await API.post('ShipyardAPI', moveConfig.getURL(moveConfig.applicationInstanceId), {
                body: {
                    FilterString: buildFilterString().join(' OR '),
                    Sort: moveConfig.sort,
                    RecOffset: 0,
                },
            });
            const buildColumns = (headerRes) => {
                return Object.entries(headerRes).map(([key, value]) => ({
                    label: t(value),
                    id: key,
                }));
            };
            setSearchKeys(res.searchKeys);
            setTargetHeaders(buildColumns(res?.targetHeaders));
            setMoveListHeaders(buildColumns(res?.moveListHeaders));
            setTarget({ ...res?.target, appInfo: res?.targetAppInfo });
            setModalQuestions(
                res?.modalQuestions?.map
                    ? res.modalQuestions.map((question, i) => {
                          return {
                              ...question,
                              value: '',
                              onChange: (val) => {
                                  setModalQuestions((prev) => {
                                      return prev.map((oldQuestion) => {
                                          if (oldQuestion.label !== question.label) return oldQuestion;
                                          else {
                                              return {
                                                  ...oldQuestion,
                                                  value: val,
                                              };
                                          }
                                      });
                                  });
                              },
                          };
                      })
                    : []
            );
            setMoveList(res?.moveList);
        } catch (error) {
            toast.error(t('General.AjaxError'));
        } finally {
            setDoingAjax(false);
        }
    };

    const debouncedGet = useDebounce((term) => getRecords(term));
    const moveRecord = async () => {
        if (!!moveConfig?.nextHandlerOverride) {
            return moveConfig?.nextHandlerOverride(selectedRecordToMerge.UserID, setDoingAjax, modalQuestions);
        }
        let id = toast.loading('Record move in progress...');
        try {
            setDoingAjax(true);

            const res = await API.post('ShipyardAPI', moveConfig.updateURL(moveConfig.applicationInstanceId), {
                body: moveConfig.buildBody(target, selectedRecordToMerge, modalQuestions),
            });
            if (res.Success || res.success) {
                toast.dismiss(id);
                toast.success('Application move successful!');

                if (Number.isInteger(res?.AppInstId)) {
                    history.push(`/scholarship/application/${res.AppInstId}?admin`);
                } else {
                    history.goBack();
                }
            }
        } catch (e) {
            toast.dismiss(id);
            toast.error(t(e?.response?.data ? e.response.data : 'General.AjaxError'));
            setDoingAjax(false);
        }
    };

    React.useEffect(() => {
        debouncedGet(searchTerm);
    }, [searchTerm]);

    return (
        <DetailsPage
            rightText={moveConfig?.moveViewTitle}
            onBackClick={handleBackClick}
            stepperForwardHandler={() => setConfirmModalIsOpen(true)}
            buttonText={moveConfig?.buttonText}
        >
            <PageSection
                headerText={moveConfig?.label1}
                descriptionText={moveConfig?.description1}
            >
                <Box sx={{ marginBottom: '24px' }}>
                    <Typography
                        style={{
                            marginTop: '8px',
                            opacity: 1,
                            color: 'rgba(125,125,125,1)',
                            fontFamily:
                                "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                            fontSize: '13px',
                            fontWeight: 700,
                            fontStyle: 'normal',
                            letterSpacing: '0px',
                            textAlign: 'left',
                        }}
                    >
                        {moveConfig?.overrideWarning ? (
                            moveConfig.overrideWarning
                        ) : (
                            <>
                                <span style={{ color: '#D24444', fontWeight: 700, fontSize: '14px' }}>PLEASE BE AWARE:</span> This tool
                                should only be used to move an application for a student <span>if</span> the user whom owns the destination
                                student is allowed to view the application data for the origination student. <b>ALL</b> information in the
                                application, including parent name, address, phone number, email, social security number etc. will be
                                viewable and accessible by the user that owns the destination student record.
                            </>
                        )}
                    </Typography>
                </Box>
                {/* <ActionDrivenTable data={[target] || []} columns={dataKey === 'UniqueID' ? [...targetHeaders, {label: 'Application', id:'viewApplication'}] : targetHeaders} /> */}
                <ActionDrivenTable
                    data={[target] || []}
                    columns={targetHeaders}
                />
            </PageSection>
            <Divider sx={{ margin: '24px' }} />
            <PageSection
                headerText={moveConfig?.label2}
                descriptionText={moveConfig?.description2}
            >
                <Input
                    onChange={(e) => setSearchTerm(e.target.value)}
                    value={searchTerm || moveConfig?.searchTerm}
                    placeholder={moveConfig?.inputPlaceholder}
                    style={{ width: '440px', fontSize: '13px', fontWeight: 600, color: '#9B9B9B' }}
                    startAdornment={<HiOutlineSearch style={{ paddingRight: '8px', color: '#014E8D', fontSize: '18px' }} />}
                />
                <Typography
                    style={{
                        opacity: 1,
                        color: 'rgba(1,78,141,1)',
                        fontFamily: 'SFProDisplay-SemiboldItalic',
                        fontSize: '13px',
                        fontWeight: 600,
                        fontStyle: 'italic',
                        letterSpacing: '0px',
                        textAlign: 'left',
                        marginTop: '24px',
                        marginBottom: '8px',
                    }}
                >
                    {moveConfig?.possibleMatchesLanguage}
                </Typography>
                <ActionDrivenTable
                    data={moveList}
                    columns={moveListHeaders}
                    rowSelectionHandler={(rowData) => setSelectedRecordToMerge(rowData)}
                    selectedItem={selectedRecordToMerge}
                    dataKey={moveConfig?.dataKey}
                />
            </PageSection>
            <Dialog open={confirmModalIsOpen}>
                <DialogTitle>{moveConfig?.modalTitle}</DialogTitle>
                <DialogContent>
                    <Box>
                        <Typography>{moveConfig.buildConfirmDescription(target, selectedRecordToMerge, modalContentOverride)}</Typography>
                        <RenderIf isTrue={!moveConfig?.shouldRenderModalContent}>
                            <Typography
                                sx={{
                                    ...modalContentOverride,
                                    marginTop: moveConfig.buildConfirmDescription(target, selectedRecordToMerge, modalContentOverride)
                                        ? '24px'
                                        : null,
                                }}
                            >
                                Click{' '}
                                <Typography
                                    component={'span'}
                                    sx={{ color: 'rgba(1,78,141, 1)', fontWeight: 700 }}
                                >
                                    {moveConfig?.confirmText}
                                </Typography>{' '}
                                to complete the below actions:
                            </Typography>
                            <Box>{moveConfig.buildComparison(target, selectedRecordToMerge, modalContentList)}</Box>
                            <Typography sx={modalContentOverride}>
                                Click{' '}
                                <Typography
                                    component={'span'}
                                    sx={{ color: '#D74242', fontWeight: 700 }}
                                >
                                    {moveConfig?.cancelText}
                                </Typography>{' '}
                                to cancel the {moveConfig?.action || 'merge'}.
                            </Typography>
                        </RenderIf>
                        <RenderIf isTrue={!!moveConfig?.shouldRenderModalContent && !!modalQuestions.length}>
                            {moveConfig?.renderModalContent(modalQuestions, setModalQuestions)}
                        </RenderIf>
                    </Box>
                    <Box>
                        <Button
                            onClick={() => {
                                if (!!moveConfig?.cancelHandlerOverride) {
                                    moveConfig.cancelHandlerOverride();
                                }
                                setConfirmModalIsOpen(false);
                            }}
                            variant="contained"
                            disabled={doingAjax}
                        >
                            {moveConfig?.cancelText}
                        </Button>
                        <Button
                            onClick={() => {
                                moveRecord();
                            }}
                            variant="contained"
                            disabled={doingAjax}
                        >
                            {moveConfig?.confirmText}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </DetailsPage>
    );
};

export default MoveRecordPage;

const modalContentOverride = {
    color: 'rgba(109,114,120,1)',
    fontSize: '15px',
    fontWeight: 600,
    fontStyle: 'normal',
    letterSpacing: '0px',
    textAlign: 'left',
};

const modalContentList = {
    color: 'rgba(35,28,50,1)',
    fontSize: '15px',
    fontWeight: 400,
    fontStyle: 'normal',
    letterSpacing: '0px',
    textAlign: 'left',
};

export const comparisonHelper = (target, selectedRecordToMerge) => {
    if (!selectedRecordToMerge || !target) return {};

    if (target.FirstName) {
        return {
            target: {
                fullName: `${target['FirstName']} ${target['LastName']}`,
                ID: target.UserID,
            },
            selectedRecordToMerge: {
                fullName: `${selectedRecordToMerge['User First Name']} ${selectedRecordToMerge['User Last Name']}`,
                id: selectedRecordToMerge.UserID,
            },
        };
    }
    return {
        target: {
            fullName: `${target['First Name']} ${target['Last Name']}`,
            typeID: target['Type ID'],
        },
        selectedRecordToMerge: {
            fullName: `${selectedRecordToMerge['Applicant First Name']} ${selectedRecordToMerge['Applicant Last Name']}`,
            guardianFullName: `${selectedRecordToMerge['Initiating User First Name']} ${selectedRecordToMerge['Initiating User Last Name']}`,
            id: selectedRecordToMerge['Unique ID'],
            email: `${selectedRecordToMerge['Initiating User Email Address']}`,
        },
    };
};
